<template>
  <div class="card-list">
    <div class="card-list__wrap">
      <ExcursionCardListSkeleton>
        <template
          v-for="(exp, index) in expList"
          :key="exp.id"
        >
          <ExcursionCardSchemaOrgWrapper
            :exp="exp"
            :is-show-schema-org="true"
          >
            <ExpCard
              :exp="exp"
              :exp-snippet-index="index"
              :exp-url-params="selectedRubricUrl"
              :context-geo-subject="contextGeoSubject"
              :show-type="showType"
              :is-user-admin="userStore.isAdmin"
              :target-blank="isTargetBlank"
              :type-label="undefined"
              :sogu-block="soguBlock"
              sogu-page="listing"
              :additional-sogu-params="listingCardSoguParams"
              class="exp-snippet"
              :allow-show-promotion-badge="true"
              :tooltip-teleport-target="targetModalElem"
              :is-in-favorites="isFavorite(exp.id)"
              is-show-favorite-btn
              :isOptimizedImage="getIsOptimizedImage(index)"
              :labels="isListingLabelsV3ExperimentActive ? exp.labels : undefined"
              :hidden-fields="fieldsToHide"
              :showMiniDiscount="isExpCardMiniDiscountActive"
              @toggle-favorite="toggleFavoriteHandle(exp)"
              @right-clicked="handleRightClick($event, exp)"
            />
          </ExcursionCardSchemaOrgWrapper>
          <slot :index="index" />
        </template>
      </ExcursionCardListSkeleton>
    </div>
  </div>
</template>

<script setup lang="ts">
  // Widgets
  import ExcursionCardListSkeleton from './ExcursionCardListSkeleton.vue';
  import ExcursionCardSchemaOrgWrapper from '@/widgets/allCardsBlock/ui/excursion/ExcursionCardSchemaOrgWrapper.vue';

  // Features
  import { useToggleExperienceFavorite } from '@/features/experience-favorite';
  import { useExcursionExperienceFavoriteSogu } from '@/features/experience-favorite/composables/excursion/useExcursionExperienceFavoriteSogu.ts';
  import { useListingLabelsV3Experiment } from '@/features/experiments';

  // Entities
  import { useUserStore } from '@/entities/user';
  import { mapExperienceDataToSogu } from '@/entities/experience/sogu';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { trackEvent } from '@/entities/sogu';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';

  // Shared
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import type { RecommendationListingSoguParams } from '@/shared/types/recommendationListingSogu';

  // External and shared packages
  import { getBootstrapData, useGrowthbook } from '@experience/core-hostapp-share-lib';
  import { ExpCard } from '@experience/exp-card';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import { useScreenSize } from '@experience/shared';
  import { computed, ref, type useShadowRoot } from 'vue';
  import { useExpCardMiniDiscount } from '@/features/experiments/composables/useExpCardMiniDiscount';

  interface Props {
    expList: Experience[];
    contextGeoSubject: ContextGeoSubject;
    showType: boolean;
    soguBlock?: string;
    recommendationListingSoguParams?: RecommendationListingSoguParams;
  }

  const props = withDefaults(defineProps<Props>(), {
    soguBlock: 'experiences',
  });

  const userStore = useUserStore();

  const { isMobile, isTablet } = useScreenSize();
  const { isFavorite } = useExperienceFavorites();
  const { trackExperiencesFavoriteClick } = useExcursionExperienceFavoriteSogu();
  const { toggle } = useToggleExperienceFavorite();
  const filtersStore = useExcursionFiltersStore();
  const { isListingLabelsV3ExperimentActive } = useListingLabelsV3Experiment();
  const gb = useGrowthbook();
  const bootstrapData = getBootstrapData();

  const isTargetBlank = computed(() => !isMobile.value && !isTablet.value);

  const fieldsToHide = computed(() => ({
    duration: false,
    price: false,
    rating: false,
    reviews: false,
    guide: false,
    description: Boolean(gb?.isOn('hide_exp_card_tagline')) && !bootstrapData.prerenderMode,
    dates: false,
  }));

  const getIsOptimizedImage = (index: number) => {
    if (isMobile.value) {
      return index === 0;
    }

    return [0, 1].includes(index);
  };

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const modalTarget = ref<HTMLElement | null>(null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  }

  const targetModalElem = computed(() => modalTarget.value || document.body);

  const selectedRubricUrl = computed(() => {
    const rubric = filtersStore.state.tag__slugs;

    return rubric && rubric !== 'all' ? `?rubric=${rubric}` : '';
  });

  const listingCardSoguParams = computed(() => props.recommendationListingSoguParams ?? {});

  const handleRightClick = (clickType: string, slide: Experience) => {
    const payload = {
      clickType,
      ...props.contextGeoSubject.soguStamp,
      ...mapExperienceDataToSogu(slide),
    };
    trackEvent('listing:experiences:experience.right_click', {
      ...payload,
    });
  };

  const toggleFavoriteHandle = (exp: Experience) => {
    trackExperiencesFavoriteClick(exp, props.contextGeoSubject);
    toggle(exp.id);
  };

  const { isExpCardMiniDiscountActive } = useExpCardMiniDiscount();
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  :has(.paused-output) .card-list {
    margin-top: 32px;
  }

  .card-list__wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    @supports (display: grid) {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 40px 24px;

      @include adaptivity.tablet {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include adaptivity.mobile {
        grid-template-columns: minmax(0, 1fr);
        gap: 26px 24px;
      }

      @media only screen and (min-width: 1024px) and (max-width: 1075px) {
        grid-template-columns: repeat(auto-fit, minmax(300px, 0.333fr));
      }
    }
  }
</style>
